import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { CustomImageExtension } from '../extensions/CustomImageExtension';
import { LayoutExtension } from '../extensions/LayoutExtension';
import { AuthoredContentExtension } from '../extensions/AuthoredContentExtension';
import { ReadonlyTextExtension } from '../extensions/ReadonlyTextExtension';
import { ImagePlaceholderExtension } from '../extensions/ImagePlaceholderExtension';
import { FeedbackHighlightExtension } from '../pages/sop-editor/FeedbackHighlightExtension';

export const useEditorConfig = (
  content: string,
  onUpdate: (content: string) => void,
  onCreate?: () => void,
  getCurrentUrl?: (imageId: string) => string | undefined,
  editable = true,
) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc list-inside',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal',
          },
        },
        link: {
          openOnClick: false,
          enableClickSelection: true,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
            class: 'editor-link',
          },
        },
      }),
      TaskList.configure({
        HTMLAttributes: {
          class: 'list-none list-inside space-y-1',
        },
      }),
      TaskItem.configure({
        nested: true,
        taskListTypeName: 'taskList',
        HTMLAttributes: {
          class: 'flex items-start gap-2',
        },
      }),
      Placeholder.configure({
        placeholder: 'Enter step details...',
      }),
      CustomImageExtension.configure({
        HTMLAttributes: {
          class: 'custom-image',
          loading: 'lazy',
          decoding: 'async',
        },
        getCurrentUrl,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: 'left',
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'tiptap-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      LayoutExtension.configure({
        HTMLAttributes: {
          class: 'layout-container',
        },
      }),
      AuthoredContentExtension,
      ReadonlyTextExtension,
      ImagePlaceholderExtension,
      FeedbackHighlightExtension,
    ],
    content,
    onCreate: () => {
      onCreate?.();
    },
    onUpdate: ({ editor }) => {
      onUpdate(editor.getHTML());
    },
    editable,
    shouldRerenderOnTransaction: true,
  });

  return editor;
};